import React, { useState, useEffect } from "react";
import { useTranslation } from "../../../../i18n/LanguageContext";
import PhoneNumberScamCheckAPI from "../../services/PhoneNumberScamCheckAPI";
import ScamReportAPI from "../../services/ScamReportAPI";
import { isHighRisk, isMediumRisk } from "./textAnalysisUtils";
import ReadOnlyBanner from "../../common/ReadOnlyBanner";
import PhoneNumberScamCheckDesktop from "./PhoneNumberScamCheckDesktop";
import PhoneNumberScamCheckMobile from "./PhoneNumberScamCheckMobile";

function PhoneNumberScamCheck() {
  const { t } = useTranslation();
  // Mobile detection constant
  const isMobile = () => window.innerWidth <= 768;

  const [phoneNumber, setPhoneNumber] = useState("");
  const [context, setContext] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasUserInput, setHasUserInput] = useState(false);
  const [phoneValidation, setPhoneValidation] = useState({
    isValid: true,
    isComplete: false,
  });
  const [reportId, setReportId] = useState(null);
  const [showMobileView, setShowMobileView] = useState(isMobile());
  const [phoneInputKey, setPhoneInputKey] = useState(0);

  // Track window resize for mobile detection
  useEffect(() => {
    const handleResize = () => setShowMobileView(isMobile());
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handlePhoneChange = (e164Value, validationInfo) => {
    setPhoneNumber(e164Value);

    if (validationInfo) {
      setPhoneValidation({
        isValid: validationInfo.isValid,
        isComplete: validationInfo.isComplete,
      });
    }

    setHasUserInput(e164Value && e164Value.length > 2);
  };

  const handleClearSearch = () => {
    setPhoneNumber("");
    setContext("");
    setHasUserInput(false);
    setSearchResults(null);
    setError(null);
    setPhoneInputKey((k) => k + 1);
  };

  const handleSearch = async () => {
    const digits = phoneNumber.replace(/\D/g, "");

    if (!digits) {
      setError(t("phoneCheck.error.enterPhone"));
      return;
    }

    if (!phoneValidation.isValid) {
      setError(t("phoneCheck.error.invalidPhone"));
      return;
    }

    if (!phoneValidation.isComplete) {
      setError(t("phoneCheck.error.incompletePhone"));
      return;
    }

    setIsLoading(true);
    setError(null);
    setSearchResults(null);

    try {
      const response = await PhoneNumberScamCheckAPI.analyzePhoneNumber(
        digits,
        context,
      );

      if (response.data) {
        setSearchResults(response.data);
      } else {
        setSearchResults(response);
      }

      // Save scam report immediately when scam detected
      const result = response.data || response;
      if (
        result.isScam === true ||
        isHighRisk(result.riskLevel) ||
        isMediumRisk(result.riskLevel)
      ) {
        try {
          const saveResponse = await ScamReportAPI.saveReport({
            analysisType: "PHONE_NUMBER_CHECK",
            analysisTitle: "Phone Number Scam Detected",
            analysisDetail: `Phone: ${digits}`,
            analysisInput: digits,
            context: context || undefined,
            isScam: result.isScam,
            confidence: result.confidence,
            riskLevel: result.riskLevel,
            explanation: result.summary || "",
            suggestions: Array.isArray(result.recommendations)
              ? result.recommendations.join("\n")
              : result.recommendations || "",
          });
          if (saveResponse?.reportId) setReportId(saveResponse.reportId);
        } catch (saveErr) {
          console.error("Failed to save scam report:", saveErr);
        }
      }
    } catch (err) {
      setError(err.message || t("phoneCheck.error.generic"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && hasUserInput && !isLoading) {
      handleSearch();
    }
  };

  const sharedProps = {
    phoneNumber,
    context,
    setContext,
    searchResults,
    isLoading,
    error,
    hasUserInput,
    phoneValidation,
    reportId,
    handlePhoneChange,
    handleSearch,
    handleClearSearch,
    handleKeyPress,
    phoneInputKey,
  };

  return (
    <>
      <ReadOnlyBanner />
      {showMobileView ? (
        <PhoneNumberScamCheckMobile {...sharedProps} />
      ) : (
        <PhoneNumberScamCheckDesktop {...sharedProps} />
      )}
    </>
  );
}

export default PhoneNumberScamCheck;
